import { useEffect, useState, useCallback } from 'react'
import { FieldValues, UseFormSetValue } from 'react-hook-form'

import { Box, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import { useCustomFieldPermissions } from 'hooks/useCustomFieldPermissions'
import { usePermission } from 'hooks/usePermission'
import {
  getActiveMerchantUuid,
  getContextMerchantUuid
} from 'lib/merchant-group'
import Modal from 'templates/Settings/Body/Sections/ExtraFields/components/ExtraFieldModal/ExtraFields.Modal'
import { useExtraFieldsContext } from 'templates/Settings/Body/Sections/ExtraFields/Extrafields.context'
import { ExtraFieldsType } from 'types/CustomFields'

import AutocompleteAdd from 'components/AutocompleteAdd/AutocompleteAdd'
import Upgrade from 'components/Upgrade'

import { theme } from 'styles/theme'

type Record = {
  label: string
  value: string
  type?:
    | 'text'
    | 'checkbox'
    | 'radio'
    | 'accept'
    | 'upload'
    | 'long_text'
    | 'short_text'
    | 'date'
  required: boolean
  isExcluded?: boolean
}

type ExtraFieldsAutocompleteProps<T extends FieldValues> = {
  value: Omit<Record, 'required'> | null
  onChange: any
  setValue?: UseFormSetValue<T>
  errorMessage?: string
  showAddButton?: boolean
  excludedValues?: string[]
  excludeFieldTypes?: string[]
  setExtraFieldsList: React.Dispatch<
    React.SetStateAction<ExtraFieldsType | undefined>
  >
  onFieldCreationStart?: () => void
  onFieldCreationEnd?: () => void
}

// Cache a nível de módulo para evitar refetch a cada mount/remount. Por
// empresa (SC-10999): na visão multi-empresas o cadastro de cliente dentro do
// formulário de cobrança usa a empresa escolhida, sem recarregar a página.
const customFieldsCache = new Map<string, any[]>()
const customFieldsCacheKey = () =>
  getActiveMerchantUuid() || getContextMerchantUuid() || 'context'
export const invalidateCustomFieldsCache = () => {
  customFieldsCache.clear()
}
export const customFieldsMapper = {
  short_text: 'Texto curto',
  long_text: 'Texto longo',
  checkbox: 'Multi-seleção',
  radio: 'Seleção única',
  accept: 'Termos e condições',
  upload: 'Upload de arquivo',
  date: 'Data'
}
const ExtraFieldsAutocomplete = <T extends FieldValues>({
  value,
  onChange,
  setValue,
  errorMessage,
  showAddButton = true,
  excludedValues = [],
  excludeFieldTypes = [],
  setExtraFieldsList,
  onFieldCreationStart,
  onFieldCreationEnd
}: ExtraFieldsAutocompleteProps<T>) => {
  const [extraFields, setExtraFields] = useState<Record[]>([])
  const {
    edit: { handleOpen, handleClose, open }
  } = useExtraFieldsContext()
  const { getFieldTypePermission } = useCustomFieldPermissions()
  // GET /v1/custom_fields/all exige custom_fields:show. Sem ela a busca só
  // gerava 403 e o toast "Sem permissão" ao abrir a ficha do cliente.
  const canShowCustomFields = usePermission('custom_fields:show')

  const [customFields, setCustomFields] = useState<any[]>(
    customFieldsCache.get(customFieldsCacheKey()) || []
  )

  const fetchCustomFields = useCallback(async () => {
    try {
      const response = await DataService({
        type: 'GET',
        url: '/v1/custom_fields/all'
      })

      customFieldsCache.set(customFieldsCacheKey(), response.data.records)
      setCustomFields(response.data.records)
    } catch (error) {
      console.error('Erro ao buscar campos extras:', error)
    }
  }, [])

  useEffect(() => {
    if (!customFieldsCache.get(customFieldsCacheKey()) && canShowCustomFields) {
      fetchCustomFields()
    }
  }, [canShowCustomFields])
  useEffect(() => {
    if (customFields && customFields.length > 0) {
      try {
        setExtraFieldsList(customFields as any)

        const fieldTypeFromInt: { [key: number]: string } = {
          0: 'short_text',
          1: 'radio',
          2: 'checkbox',
          3: 'accept',
          4: 'upload',
          5: 'long_text',
          6: 'date'
        }

        const records = customFields.map((field) => {
          const resolvedType =
            typeof field.field_type === 'number'
              ? fieldTypeFromInt[field.field_type] || 'short_text'
              : field.field_type

          return {
            label: field.field_configuration.title,
            value: field.uuid,
            type: resolvedType as
              | 'text'
              | 'checkbox'
              | 'radio'
              | 'accept'
              | 'upload'
              | 'long_text'
              | 'short_text'
              | 'date',
            required: false,
            isExcluded: excludeFieldTypes.includes(resolvedType)
          }
        })

        const orderedFieldTypes = [
          'short_text',
          'long_text',
          'checkbox',
          'radio',
          'accept',
          'upload',
          'date'
        ] as const

        const orderedRecords = orderedFieldTypes.flatMap((type) => {
          const filtered = records.filter((r) => r.type === type)

          if (filtered.length === 0) return []

          const headerValue = `${type}_header` as const

          return [
            ...(showAddButton
              ? [
                  {
                    label: customFieldsMapper[type],
                    value: headerValue,
                    required: false
                  }
                ]
              : []),
            ...filtered
          ]
        })

        setExtraFields(orderedRecords)
      } catch (error) {
        console.error('Erro ao processar campos extras:', error)
      }
    }
  }, [customFields, showAddButton, excludeFieldTypes, setExtraFieldsList])
  return (
    <>
      <AutocompleteAdd
        externalControllers={{
          handleCloseModal: handleClose,
          handleOpenModal: () => {
            handleOpen()
            onFieldCreationStart?.()
          },
          open: open
        }}
        showAddButton={showAddButton}
        addPermission="custom_fields:manage"
        key={value?.value}
        value={value}
        onChange={onChange}
        setValue={setValue}
        errorMessage={errorMessage}
        addButtonText="Novo campo"
        label=""
        placeholder="Selecione um campo extra"
        modalTitle="Novo campo extra"
        options={extraFields || []}
        getOptionDisabled={(option: any) =>
          [
            'upload_header',
            'accept_header',
            'radio_header',
            'checkbox_header',
            'long_text_header',
            'short_text_header',
            'date_header'
          ].includes(option.value) || excludedValues.includes(option.value)
        }
        renderOption={(props: any, option: any) => {
          const optionTyped = option as Record
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { style, sx, onClick, ...rest } = props

          if (
            [
              'upload_header',
              'accept_header',
              'radio_header',
              'checkbox_header',
              'long_text_header',
              'short_text_header',
              'date_header'
            ].includes(optionTyped.value)
          ) {
            // Extrair o tipo do campo do valor do header
            const fieldType = optionTyped.value.replace('_header', '') as any
            const fieldPermission = getFieldTypePermission(fieldType)
            const isFieldTypeExcluded = excludeFieldTypes.includes(fieldType)

            return (
              <Box
                component="li"
                sx={{
                  all: 'unset',
                  bgcolor: theme.palette.neutral[700],
                  fontWeight: 600,
                  pointerEvents: fieldPermission.isDisabled ? 'auto' : 'none',
                  userSelect: 'none',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '8px 12px'
                }}
                aria-disabled={true}
                data-option-index={-1}
                {...rest}
              >
                <Box display="flex" alignItems="center">
                  <span style={{ marginRight: '8px' }}>
                    {optionTyped.label}
                  </span>
                  {isFieldTypeExcluded && (
                    <Typography
                      component="span"
                      sx={{
                        ml: -0.5,
                        mt: 0.2,
                        fontSize: '12px',
                        color: theme.palette.neutral[500]
                      }}
                    >
                      (não permitido neste contexto)
                    </Typography>
                  )}
                </Box>
                {fieldPermission.isDisabled && (
                  <Upgrade
                    plan={fieldPermission.plan as any}
                    resourceName={`Campo ${optionTyped.label}`}
                  />
                )}
              </Box>
            )
          }

          const isAlreadyAdded = excludedValues.includes(optionTyped.value)
          const fieldPermission = getFieldTypePermission(
            optionTyped.type as any
          )
          const isExcluded = (optionTyped as any).isExcluded
          const isDisabled =
            isAlreadyAdded || fieldPermission.isDisabled || isExcluded

          return (
            <Box
              component="li"
              sx={{
                all: 'unset',
                ...(isDisabled && {
                  opacity: 0.5,
                  pointerEvents: 'none',
                  color: theme.palette.neutral[500]
                }),
                fontSize: '14px'
              }}
              onClick={isDisabled ? undefined : onClick}
              {...rest}
            >
              {optionTyped.label}
              {isAlreadyAdded && (
                <Typography
                  component="span"
                  sx={{
                    ml: 0.4,
                    mt: 0.2,
                    fontSize: '12px',
                    color: theme.palette.neutral[500]
                  }}
                >
                  (já adicionado)
                </Typography>
              )}
            </Box>
          )
        }}
        modalBodyComponent={
          <Modal
            onFieldCreated={async (newFieldUuid) => {
              try {
                // Refetch dos campos após criação para atualizar o autocomplete
                await fetchCustomFields()

                // Após atualizar a lista, selecionar automaticamente o novo campo
                if (newFieldUuid) {
                  // Buscar o campo recém-criado diretamente da API para garantir dados atualizados
                  const response = await DataService({
                    type: 'GET',
                    url: '/v1/custom_fields/all'
                  })

                  const newField = response.data.records.find(
                    (field: any) => field.uuid === newFieldUuid
                  )

                  if (newField) {
                    const fieldOption = {
                      label: newField.field_configuration.title,
                      value: newField.uuid,
                      type: newField.field_type,
                      required: false,
                      // SC-11348: a lista do onChange pode estar desatualizada
                      // aqui; o upload precisa da configuração do campo novo
                      field_configuration: newField.field_configuration,
                      upload_settings: newField.upload_settings
                    }
                    // Selecionar automaticamente o novo campo
                    onChange(null, fieldOption)
                  }
                }
              } catch (error) {
                console.error('Erro ao selecionar campo recém-criado:', error)
              } finally {
                // Finalizar loading independente de sucesso ou erro
                onFieldCreationEnd?.()
              }
            }}
            onModalClose={() => {
              // Finalizar loading quando modal for fechado sem criar
              onFieldCreationEnd?.()
            }}
          />
        }
        closeModalFunctionName="handleClose"
      />
    </>
  )
}

export default ExtraFieldsAutocomplete
